<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import AdminNavigation from '@/components/admin/AdminNavigation.vue'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Loader2, RefreshCw, AlertCircle, ChevronDown, CheckCircle, XCircle } from 'lucide-vue-next'
import { API, HOST, HOST_TULUM } from '@/utils/http'

interface PlanetSubscription {
  id: number
  plan_name: string | null
  plan_state: string | null
  state: string | null
  start_time: string | null
  end_time: string | null
  category: string | null
  quota_sqkm: number | null
  used_sqkm: number | null
  remaining_sqkm: number | null
  basemap_quad_quota: number | null
  basemap_quad_used: number | null
  scene_quad_quota: number | null
  scene_quad_used: number | null
  extra_fields: Record<string, any> | null
}

interface TaskingContract {
  id: string | null
  name: string | null
  sfdc: string | null
  state: string | null
  start_date: string | null
  expiration_date: string | null
  quota_limit: number | null
  quota_used: number | null
  quota_remaining: number | null
  quota_units: string | null
  assured_quota_limit: number | null
  overage: boolean | null
  extra_fields: Record<string, any> | null
}

interface PlanetProduct {
  id: number
  name: string | null
  title: string | null
  description: string | null
  product_family: string | null
  quota_total: number
  quota_used: number
  quota_remaining: number
  quota_units: string | null
  unlimited_quota: boolean
  supports_reservation: boolean
  extra_fields: Record<string, any> | null
}

interface Organization {
  organization_id: number
  organization_name: string
}

interface PendingLayer {
  code: string
  layer_type: string
  layer_name: string | null
  org_id: number
  member_id: number | null
  status: number
  status_label: string
  created_at: string
  data: Record<string, any> | null
  approved_at?: string | null
  rejection_reason?: string | null
}

const store = useStore()
const router = useRouter()
const member = computed(() => store.getters.getMember)

// Organizations state
const organizations = ref<Organization[]>([])
const organizationsLoading = ref(false)
const selectedOrgId = ref<string | null>(null)

// Data state
const subscriptions = ref<PlanetSubscription[]>([])
const taskingContracts = ref<TaskingContract[]>([])
const products = ref<PlanetProduct[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Pending layers state
const pendingLayers = ref<PendingLayer[]>([])
const historyLayers = ref<PendingLayer[]>([])
const pendingLoading = ref(false)
const historyLoading = ref(false)
const pendingError = ref<string | null>(null)
const historyError = ref<string | null>(null)
const rejectDialogOpen = ref(false)
const rejectingLayer = ref<PendingLayer | null>(null)
const rejectReason = ref('')
const processingAction = ref<string | null>(null)

// Main section tab (Layers or Products)
const mainSection = ref('layers')

// Sub-tabs
const layersTab = ref('pending')
const productsTab = ref('downloads')

// Track expanded state
const expandedItems = ref<Set<string>>(new Set())

const toggleExpanded = (id: string) => {
  if (expandedItems.value.has(id)) {
    expandedItems.value.delete(id)
  } else {
    expandedItems.value.add(id)
  }
}

const isExpanded = (id: string) => expandedItems.value.has(id)

// Computed filtered subscriptions by category
const downloadSubscriptions = computed(() =>
  subscriptions.value.filter(s =>
    s.plan_name?.toLowerCase().includes('skysat') ||
    s.plan_name?.toLowerCase().includes('ps_re') ||
    s.plan_name?.toLowerCase().includes('download') ||
    (!s.plan_name?.toLowerCase().includes('mosaic') &&
     !s.plan_name?.toLowerCase().includes('basemap') &&
     !s.plan_name?.toLowerCase().includes('stream') &&
     s.quota_sqkm !== null)
  )
)

const streamingSubscriptions = computed(() =>
  subscriptions.value.filter(s =>
    s.plan_name?.toLowerCase().includes('mosaic') ||
    s.plan_name?.toLowerCase().includes('basemap') ||
    s.plan_name?.toLowerCase().includes('stream') ||
    s.basemap_quad_quota !== null ||
    s.scene_quad_quota !== null
  )
)

const otherProducts = computed(() => products.value)

const fetchOrganizations = async () => {
  organizationsLoading.value = true
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/admin/organizations`,
      params: { limit: 500 }
    })
    organizations.value = response.data
  } catch (err: any) {
    console.error('Error fetching organizations:', err)
  } finally {
    organizationsLoading.value = false
  }
}

const fetchPendingLayers = async () => {
  pendingLoading.value = true
  pendingError.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/admin/pending-layers`
    })
    pendingLayers.value = response.data
  } catch (err: any) {
    console.error('Error fetching pending layers:', err)
    pendingError.value = err.response?.data?.detail || 'Failed to fetch pending layers'
  } finally {
    pendingLoading.value = false
  }
}

const fetchHistoryLayers = async () => {
  historyLoading.value = true
  historyError.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/admin/layers-history`
    })
    historyLayers.value = response.data
  } catch (err: any) {
    console.error('Error fetching layer history:', err)
    historyError.value = err.response?.data?.detail || 'Failed to fetch layer history'
  } finally {
    historyLoading.value = false
  }
}

const approveLayer = async (layer: PendingLayer) => {
  processingAction.value = layer.code
  try {
    await API({
      method: 'POST',
      url: `${HOST}/admin/layers/${layer.code}/approve`
    })
    // Remove from pending list and refresh history
    pendingLayers.value = pendingLayers.value.filter(l => l.code !== layer.code)
    fetchHistoryLayers()
  } catch (err: any) {
    console.error('Error approving layer:', err)
    pendingError.value = err.response?.data?.detail || 'Failed to approve layer'
  } finally {
    processingAction.value = null
  }
}

const openRejectDialog = (layer: PendingLayer) => {
  rejectingLayer.value = layer
  rejectReason.value = ''
  rejectDialogOpen.value = true
}

const confirmReject = async () => {
  if (!rejectingLayer.value || !rejectReason.value.trim()) return

  processingAction.value = rejectingLayer.value.code
  try {
    await API({
      method: 'POST',
      url: `${HOST}/admin/layers/${rejectingLayer.value.code}/reject`,
      data: { reason: rejectReason.value.trim() }
    })
    // Remove from pending list and refresh history
    pendingLayers.value = pendingLayers.value.filter(l => l.code !== rejectingLayer.value?.code)
    rejectDialogOpen.value = false
    rejectingLayer.value = null
    rejectReason.value = ''
    fetchHistoryLayers()
  } catch (err: any) {
    console.error('Error rejecting layer:', err)
    pendingError.value = err.response?.data?.detail || 'Failed to reject layer'
  } finally {
    processingAction.value = null
  }
}

const getOrgName = (orgId: number): string => {
  const org = organizations.value.find(o => o.organization_id === orgId)
  return org?.organization_name || `Org ${orgId}`
}

const getLayerTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    'order': 'Order',
    'subscription': 'Subscription',
    'tasking': 'Tasking'
  }
  return labels[type] || type
}

const fetchData = async () => {
  if (!selectedOrgId.value) {
    subscriptions.value = []
    taskingContracts.value = []
    products.value = []
    return
  }

  loading.value = true
  error.value = null

  try {
    const [subsResponse, taskingResponse, productsResponse] = await Promise.all([
      API({ method: 'GET', url: `${HOST_TULUM}/planet/${selectedOrgId.value}/subscriptions` }),
      API({ method: 'GET', url: `${HOST_TULUM}/planet/${selectedOrgId.value}/tasking-contracts` }),
      API({ method: 'GET', url: `${HOST_TULUM}/planet/${selectedOrgId.value}/products` })
    ])

    subscriptions.value = subsResponse.data.subscriptions || []
    taskingContracts.value = taskingResponse.data.contracts || []
    products.value = productsResponse.data.products || []

  } catch (err: any) {
    console.error('Error fetching data:', err)
    error.value = err.response?.data?.detail || 'Failed to fetch data'
  } finally {
    loading.value = false
  }
}

const handleOrgChange = (value: any) => {
  selectedOrgId.value = value
  expandedItems.value.clear()
  fetchData()
}

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '--'
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

const formatQuota = (value: number | null, units?: any): string => {
  if (value === null || value === undefined) return '--'
  if (units === 'sqkm' || units === 'km²') {
    return `${value.toLocaleString()} km²`
  }
  if (units === 'hectare' || units === 'ha') {
    return `${value.toLocaleString()} ha`
  }
  return value.toLocaleString()
}

const getUsagePercentage = (used: number | null, total: number | null): string => {
  if (used === null || total === null || total === 0) return '--'
  return `${Math.round((used / total) * 100)}%`
}

watch(() => member.value, (value) => {
  if (!value) return
  if (!value.is_super_user) {
    router.push({ name: 'Dashboard' })
  }
})

onMounted(() => {
  if (member.value && !member.value.is_super_user) {
    router.push({ name: 'Dashboard' })
    return
  }
  fetchOrganizations()
  fetchPendingLayers()
  fetchHistoryLayers()
})
</script>

<template>
  <div v-if="member && member.is_super_user" class="h-full flex flex-col">
    <AdminNavigation />

    <div class="flex-1 overflow-hidden px-6 py-4">
      <h1 class="text-2xl font-bold mb-4">Planet</h1>

      <!-- Main Section Tabs -->
      <Tabs v-model="mainSection" class="h-full flex flex-col">
        <TabsList class="w-fit mb-4">
          <TabsTrigger value="layers">
            Layers
          </TabsTrigger>
          <TabsTrigger value="products">
            Products
          </TabsTrigger>
        </TabsList>

        <!-- Layers Section -->
        <TabsContent value="layers" class="flex-1 overflow-hidden">
          <Tabs v-model="layersTab" class="h-full flex flex-col">
            <div class="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="pending">
                  Pending ({{ pendingLayers.length }})
                </TabsTrigger>
                <TabsTrigger value="history">
                  History
                </TabsTrigger>
              </TabsList>
              <Button
                variant="outline"
                size="sm"
                @click="layersTab === 'pending' ? fetchPendingLayers() : fetchHistoryLayers()"
                :disabled="layersTab === 'pending' ? pendingLoading : historyLoading"
              >
                <RefreshCw :class="['h-4 w-4 mr-2', { 'animate-spin': layersTab === 'pending' ? pendingLoading : historyLoading }]" />
                Refresh
              </Button>
            </div>

            <!-- Pending Tab -->
            <TabsContent value="pending" class="flex-1 overflow-auto">
              <p class="text-sm text-gray-500 mb-4">Planet layers awaiting admin approval</p>

              <div v-if="pendingLoading" class="flex items-center justify-center py-8">
                <Loader2 class="h-8 w-8 animate-spin text-gray-400" />
              </div>

              <div v-else-if="pendingError" class="p-4 bg-red-50 border border-red-200 rounded-md">
                <div class="flex items-center gap-2 text-red-700">
                  <AlertCircle class="h-5 w-5" />
                  <span>{{ pendingError }}</span>
                </div>
              </div>

              <div v-else-if="pendingLayers.length === 0" class="text-center py-8 text-gray-500">
                No pending layers requiring approval
              </div>

              <div v-else class="space-y-2">
                <div
                  v-for="layer in pendingLayers"
                  :key="layer.code"
                  class="border rounded-lg bg-white p-4"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex-1">
                      <div class="flex items-center gap-2 mb-1">
                        <Badge variant="outline">{{ getLayerTypeLabel(layer.layer_type) }}</Badge>
                        <span class="font-medium">{{ layer.layer_name || 'Unnamed Layer' }}</span>
                      </div>
                      <div class="text-sm text-gray-500 space-y-1">
                        <div>Organization: {{ getOrgName(layer.org_id) }}</div>
                        <div>Created: {{ formatDate(layer.created_at) }}</div>
                        <div v-if="layer.data" class="text-xs">
                          <span v-if="layer.data.item_type">Type: {{ layer.data.item_type }}</span>
                          <span v-if="layer.data.parameters?.id"> | Product: {{ layer.data.parameters.id }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="flex items-center gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        @click="approveLayer(layer)"
                        :disabled="processingAction === layer.code"
                      >
                        <CheckCircle v-if="processingAction !== layer.code" class="h-4 w-4 mr-1" />
                        <Loader2 v-else class="h-4 w-4 mr-1 animate-spin" />
                        Approve
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        @click="openRejectDialog(layer)"
                        :disabled="processingAction === layer.code"
                      >
                        <XCircle class="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <!-- History Tab -->
            <TabsContent value="history" class="flex-1 overflow-auto">
              <p class="text-sm text-gray-500 mb-4">Approved and rejected Planet layers</p>

              <div v-if="historyLoading" class="flex items-center justify-center py-8">
                <Loader2 class="h-8 w-8 animate-spin text-gray-400" />
              </div>

              <div v-else-if="historyError" class="p-4 bg-red-50 border border-red-200 rounded-md">
                <div class="flex items-center gap-2 text-red-700">
                  <AlertCircle class="h-5 w-5" />
                  <span>{{ historyError }}</span>
                </div>
              </div>

              <div v-else-if="historyLayers.length === 0" class="text-center py-8 text-gray-500">
                No layer history found
              </div>

              <div v-else class="space-y-2">
                <div
                  v-for="layer in historyLayers"
                  :key="layer.code"
                  class="border rounded-lg bg-white p-4"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex-1">
                      <div class="flex items-center gap-2 mb-1">
                        <Badge variant="outline">{{ getLayerTypeLabel(layer.layer_type) }}</Badge>
                        <Badge :variant="layer.status === -100 ? 'destructive' : 'default'">
                          {{ layer.status === -100 ? 'Rejected' : 'Approved' }}
                        </Badge>
                        <span class="font-medium">{{ layer.layer_name || 'Unnamed Layer' }}</span>
                      </div>
                      <div class="text-sm text-gray-500 space-y-1">
                        <div>Organization: {{ getOrgName(layer.org_id) }}</div>
                        <div>Created: {{ formatDate(layer.created_at) }}</div>
                        <div v-if="layer.approved_at">Processed: {{ formatDate(layer.approved_at) }}</div>
                        <div v-if="layer.rejection_reason" class="text-red-600">
                          Reason: {{ layer.rejection_reason }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>

        <!-- Products Section -->
        <TabsContent value="products" class="flex-1 overflow-hidden">
          <!-- Organization Selector -->
          <div class="mb-4 flex items-center gap-4">
            <div class="w-80">
              <Select @update:model-value="handleOrgChange">
                <SelectTrigger>
                  <SelectValue placeholder="Select an organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="org in organizations"
                    :key="org.organization_id"
                    :value="String(org.organization_id)"
                  >
                    {{ org.organization_name }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              v-if="selectedOrgId"
              variant="outline"
              size="sm"
              @click="fetchData"
              :disabled="loading"
            >
              <RefreshCw :class="['h-4 w-4 mr-2', { 'animate-spin': loading }]" />
              Refresh
            </Button>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="flex items-center justify-center py-12">
            <Loader2 class="h-8 w-8 animate-spin text-gray-400" />
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="p-4 bg-red-50 border border-red-200 rounded-md">
            <div class="flex items-center gap-2 text-red-700">
              <AlertCircle class="h-5 w-5" />
              <span>{{ error }}</span>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else-if="!selectedOrgId" class="text-center py-12 text-gray-500">
            Select an organization to view Planet products
          </div>

          <!-- Products Tabs -->
          <Tabs v-else v-model="productsTab" class="h-[calc(100%-60px)] flex flex-col">
            <TabsList class="w-fit">
              <TabsTrigger value="downloads">
                Downloads ({{ downloadSubscriptions.length }})
              </TabsTrigger>
              <TabsTrigger value="streaming">
                Streaming ({{ streamingSubscriptions.length }})
              </TabsTrigger>
              <TabsTrigger value="tasking">
                Tasking ({{ taskingContracts.length }})
              </TabsTrigger>
              <TabsTrigger value="other">
                Other ({{ otherProducts.length }})
              </TabsTrigger>
            </TabsList>

            <!-- Downloads Tab -->
            <TabsContent value="downloads" class="flex-1 overflow-auto mt-4">
              <div v-if="downloadSubscriptions.length === 0" class="text-center py-8 text-gray-500">
                No download subscriptions found
              </div>
              <div v-else class="space-y-2">
                <div
                  v-for="sub in downloadSubscriptions"
                  :key="`dl-${sub.id}`"
                  class="border rounded-lg bg-white"
                >
                  <Collapsible :open="isExpanded(`dl-${sub.id}`)">
                    <CollapsibleTrigger class="w-full" @click="toggleExpanded(`dl-${sub.id}`)">
                      <div class="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer">
                        <div class="flex items-center gap-4 flex-1 min-w-0">
                          <ChevronDown :class="['h-4 w-4 text-gray-500 transition-transform', { 'rotate-180': isExpanded(`dl-${sub.id}`) }]" />
                          <div class="flex-1 min-w-0">
                            <span class="font-medium truncate block">{{ sub.plan_name }}</span>
                            <span class="text-sm text-gray-500">ID: {{ sub.id }}</span>
                          </div>
                        </div>
                        <div class="flex items-center gap-8 text-sm">
                          <div class="text-right">
                            <span class="text-gray-500">Download Quota</span>
                            <div class="font-medium">{{ getUsagePercentage(sub.used_sqkm, sub.quota_sqkm) }}</div>
                          </div>
                          <div class="text-right min-w-[180px]">
                            <span class="text-gray-500">Contract Term</span>
                            <div class="font-medium">{{ formatDate(sub.start_time) }} - {{ formatDate(sub.end_time) }}</div>
                          </div>
                          <Badge :variant="sub.state === 'active' ? 'default' : 'secondary'" class="capitalize">
                            {{ sub.state }}
                          </Badge>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div class="px-4 pb-4 pt-0 border-t bg-gray-50">
                        <div class="grid grid-cols-2 gap-4 pt-4 text-sm">
                          <div>
                            <span class="text-gray-500">Quota (km²):</span>
                            <span class="ml-2 font-mono">{{ formatQuota(sub.quota_sqkm, 'km²') }}</span>
                          </div>
                          <div>
                            <span class="text-gray-500">Used (km²):</span>
                            <span class="ml-2 font-mono">{{ formatQuota(sub.used_sqkm, 'km²') }}</span>
                          </div>
                          <div v-if="sub.extra_fields" class="col-span-2">
                            <span class="text-gray-500 block mb-2">Additional Fields:</span>
                            <pre class="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32">{{ JSON.stringify(sub.extra_fields, null, 2) }}</pre>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </div>
            </TabsContent>

            <!-- Streaming Tab -->
            <TabsContent value="streaming" class="flex-1 overflow-auto mt-4">
              <div v-if="streamingSubscriptions.length === 0" class="text-center py-8 text-gray-500">
                No streaming subscriptions found
              </div>
              <div v-else class="space-y-2">
                <div
                  v-for="sub in streamingSubscriptions"
                  :key="`str-${sub.id}`"
                  class="border rounded-lg bg-white"
                >
                  <Collapsible :open="isExpanded(`str-${sub.id}`)">
                    <CollapsibleTrigger class="w-full" @click="toggleExpanded(`str-${sub.id}`)">
                      <div class="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer">
                        <div class="flex items-center gap-4 flex-1 min-w-0">
                          <ChevronDown :class="['h-4 w-4 text-gray-500 transition-transform', { 'rotate-180': isExpanded(`str-${sub.id}`) }]" />
                          <div class="flex-1 min-w-0">
                            <span class="font-medium truncate block">{{ sub.plan_name }}</span>
                            <span class="text-sm text-gray-500">ID: {{ sub.id }}</span>
                          </div>
                        </div>
                        <div class="flex items-center gap-8 text-sm">
                          <div class="text-right">
                            <span class="text-gray-500">Basemap Tile Quota</span>
                            <div class="font-medium">{{ getUsagePercentage(sub.basemap_quad_used, sub.basemap_quad_quota) }}</div>
                          </div>
                          <div class="text-right">
                            <span class="text-gray-500">Scene Tile Quota</span>
                            <div class="font-medium">{{ getUsagePercentage(sub.scene_quad_used, sub.scene_quad_quota) }}</div>
                          </div>
                          <div class="text-right min-w-[180px]">
                            <span class="text-gray-500">Contract Term</span>
                            <div class="font-medium">{{ formatDate(sub.start_time) }} - {{ formatDate(sub.end_time) }}</div>
                          </div>
                          <Badge :variant="sub.state === 'active' ? 'default' : 'secondary'" class="capitalize">
                            {{ sub.state }}
                          </Badge>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div class="px-4 pb-4 pt-0 border-t bg-gray-50">
                        <div class="grid grid-cols-2 gap-4 pt-4 text-sm">
                          <div>
                            <span class="text-gray-500">Basemap Quad Quota:</span>
                            <span class="ml-2 font-mono">{{ sub.basemap_quad_quota?.toLocaleString() ?? '--' }}</span>
                          </div>
                          <div>
                            <span class="text-gray-500">Basemap Quad Used:</span>
                            <span class="ml-2 font-mono">{{ sub.basemap_quad_used?.toLocaleString() ?? '--' }}</span>
                          </div>
                          <div>
                            <span class="text-gray-500">Scene Quad Quota:</span>
                            <span class="ml-2 font-mono">{{ sub.scene_quad_quota?.toLocaleString() ?? '--' }}</span>
                          </div>
                          <div>
                            <span class="text-gray-500">Scene Quad Used:</span>
                            <span class="ml-2 font-mono">{{ sub.scene_quad_used?.toLocaleString() ?? '--' }}</span>
                          </div>
                          <div v-if="sub.extra_fields" class="col-span-2">
                            <span class="text-gray-500 block mb-2">Additional Fields:</span>
                            <pre class="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32">{{ JSON.stringify(sub.extra_fields, null, 2) }}</pre>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </div>
            </TabsContent>

            <!-- Tasking Tab -->
            <TabsContent value="tasking" class="flex-1 overflow-auto mt-4">
              <div v-if="taskingContracts.length === 0" class="text-center py-8 text-gray-500">
                No tasking contracts found
              </div>
              <div v-else class="space-y-2">
                <div
                  v-for="contract in taskingContracts"
                  :key="`task-${contract.id}`"
                  class="border rounded-lg bg-white"
                >
                  <Collapsible :open="isExpanded(`task-${contract.id}`)">
                    <CollapsibleTrigger class="w-full" @click="toggleExpanded(`task-${contract.id}`)">
                      <div class="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer">
                        <div class="flex items-center gap-4 flex-1 min-w-0">
                          <ChevronDown :class="['h-4 w-4 text-gray-500 transition-transform', { 'rotate-180': isExpanded(`task-${contract.id}`) }]" />
                          <div class="flex-1 min-w-0">
                            <span class="font-medium truncate block">{{ contract.name || contract.sfdc }}</span>
                            <span class="text-sm text-gray-500">{{ contract.sfdc }}</span>
                          </div>
                        </div>
                        <div class="flex items-center gap-8 text-sm">
                          <div class="text-right">
                            <span class="text-gray-500">Quota</span>
                            <div class="font-medium">{{ contract.quota_limit?.toLocaleString() }} {{ contract.quota_units }}</div>
                          </div>
                          <div class="text-right">
                            <span class="text-gray-500">Used</span>
                            <div class="font-medium">{{ contract.quota_used?.toLocaleString() ?? 0 }} {{ contract.quota_units }}</div>
                          </div>
                          <div class="text-right min-w-[180px]">
                            <span class="text-gray-500">Contract Term</span>
                            <div class="font-medium">{{ formatDate(contract.start_date) }} - {{ formatDate(contract.expiration_date) }}</div>
                          </div>
                          <Badge :variant="contract.state === 'ACTIVE' ? 'default' : 'secondary'" class="capitalize">
                            {{ contract.state?.toLowerCase() }}
                          </Badge>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div class="px-4 pb-4 pt-0 border-t bg-gray-50">
                        <div class="grid grid-cols-2 gap-4 pt-4 text-sm">
                          <div>
                            <span class="text-gray-500">Remaining:</span>
                            <span class="ml-2 font-mono text-green-600">{{ contract.quota_remaining?.toLocaleString() ?? '--' }} {{ contract.quota_units }}</span>
                          </div>
                          <div>
                            <span class="text-gray-500">Usage:</span>
                            <span class="ml-2 font-mono">{{ getUsagePercentage(contract.quota_used, contract.quota_limit) }}</span>
                          </div>
                          <div>
                            <span class="text-gray-500">Assured Quota:</span>
                            <span class="ml-2 font-mono">{{ contract.assured_quota_limit?.toLocaleString() ?? 0 }} {{ contract.quota_units }}</span>
                          </div>
                          <div>
                            <span class="text-gray-500">Overage:</span>
                            <span class="ml-2 font-mono">{{ contract.overage ? 'Yes' : 'No' }}</span>
                          </div>
                          <div v-if="contract.extra_fields" class="col-span-2">
                            <span class="text-gray-500 block mb-2">Additional Fields:</span>
                            <pre class="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32">{{ JSON.stringify(contract.extra_fields, null, 2) }}</pre>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </div>
            </TabsContent>

            <!-- Other Products Tab -->
            <TabsContent value="other" class="flex-1 overflow-auto mt-4">
              <div v-if="otherProducts.length === 0" class="text-center py-8 text-gray-500">
                No other products found
              </div>
              <div v-else class="space-y-2">
                <div
                  v-for="product in otherProducts"
                  :key="`prod-${product.id}`"
                  class="border rounded-lg bg-white"
                >
                  <Collapsible :open="isExpanded(`prod-${product.id}`)">
                    <CollapsibleTrigger class="w-full" @click="toggleExpanded(`prod-${product.id}`)">
                      <div class="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer">
                        <div class="flex items-center gap-4 flex-1 min-w-0">
                          <ChevronDown :class="['h-4 w-4 text-gray-500 transition-transform', { 'rotate-180': isExpanded(`prod-${product.id}`) }]" />
                          <div class="flex-1 min-w-0">
                            <span class="font-medium truncate block">{{ product.title || product.name }}</span>
                            <span class="text-sm text-gray-500">{{ product.product_family }}</span>
                          </div>
                        </div>
                        <div class="flex items-center gap-8 text-sm">
                          <div class="text-right">
                            <span class="text-gray-500">Usage</span>
                            <div class="font-medium">
                              {{ product.unlimited_quota ? 'Unlimited' : getUsagePercentage(product.quota_used, product.quota_total) }}
                            </div>
                          </div>
                          <Badge variant="default">Active</Badge>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div class="px-4 pb-4 pt-0 border-t bg-gray-50">
                        <div class="grid grid-cols-2 gap-4 pt-4 text-sm">
                          <div v-if="product.description" class="col-span-2">
                            <span class="text-gray-500">Description:</span>
                            <p class="mt-1">{{ product.description }}</p>
                          </div>
                          <div v-if="!product.unlimited_quota">
                            <span class="text-gray-500">Quota:</span>
                            <span class="ml-2 font-mono">{{ formatQuota(product.quota_total, product.quota_units) }}</span>
                          </div>
                          <div v-if="!product.unlimited_quota">
                            <span class="text-gray-500">Used:</span>
                            <span class="ml-2 font-mono">{{ formatQuota(product.quota_used, product.quota_units) }}</span>
                          </div>
                          <div v-if="!product.unlimited_quota">
                            <span class="text-gray-500">Remaining:</span>
                            <span class="ml-2 font-mono text-green-600">{{ formatQuota(product.quota_remaining, product.quota_units) }}</span>
                          </div>
                          <div v-if="product.extra_fields" class="col-span-2">
                            <span class="text-gray-500 block mb-2">Additional Fields:</span>
                            <pre class="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32">{{ JSON.stringify(product.extra_fields, null, 2) }}</pre>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>

    <!-- Rejection Dialog -->
    <Dialog v-model:open="rejectDialogOpen">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject Layer</DialogTitle>
          <DialogDescription>
            Please provide a reason for rejecting this layer. This will be visible to the user.
          </DialogDescription>
        </DialogHeader>
        <div class="py-4">
          <Textarea
            v-model="rejectReason"
            placeholder="Enter rejection reason..."
            class="min-h-[100px]"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" @click="rejectDialogOpen = false">Cancel</Button>
          <Button
            variant="destructive"
            @click="confirmReject"
            :disabled="!rejectReason.trim() || processingAction !== null"
          >
            <Loader2 v-if="processingAction" class="h-4 w-4 mr-2 animate-spin" />
            Reject Layer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
